import React, { useEffect, useState } from 'react';
import { CheckCircle, Clock, X, ChevronLeft, Home, CreditCard, Sparkles, Calendar, MousePointer, Check, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
interface PianificaPuliziaWizardProps {
  isOpen: boolean;
  onClose: () => void;
}
interface AiSuggestion {
  id: string;
  date: string;
  dayName: string;
  dayNumber: string;
  month: string;
  time: string;
  checkOut: string;
  checkIn: string;
  type: 'standard' | 'express';
  confidence: number;
  reason: string;
  selected: boolean;
}
export function PianificaPuliziaWizard({
  isOpen,
  onClose
}: PianificaPuliziaWizardProps) {
  const [wizardMode, setWizardMode] = useState<'choice' | 'manual' | 'ai'>('choice');
  const [wizardStep, setWizardStep] = useState(0); // 0 = choice, 1-4 = manual steps, or AI steps
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  // AI mode states
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<AiSuggestion[]>([]);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  // Mock AI suggestions
  const mockAiSuggestions: AiSuggestion[] = [{
    id: 'ai-1',
    date: '2024-01-22',
    dayName: 'Lun',
    dayNumber: '22',
    month: 'Gen',
    time: '11:00 - 14:00',
    checkOut: '10:00',
    checkIn: '15:00',
    type: 'standard',
    confidence: 95,
    reason: 'Finestra ottimale tra check-out e check-in',
    selected: false
  }, {
    id: 'ai-2',
    date: '2024-01-24',
    dayName: 'Mer',
    dayNumber: '24',
    month: 'Gen',
    time: '12:00 - 14:30',
    checkOut: '11:00',
    checkIn: '15:00',
    type: 'express',
    confidence: 88,
    reason: 'Finestra stretta, consigliata pulizia express',
    selected: false
  }, {
    id: 'ai-3',
    date: '2024-01-26',
    dayName: 'Ven',
    dayNumber: '26',
    month: 'Gen',
    time: '10:00 - 14:00',
    checkOut: '09:00',
    checkIn: '15:00',
    type: 'standard',
    confidence: 92,
    reason: 'Ampia finestra disponibile',
    selected: false
  }, {
    id: 'ai-4',
    date: '2024-01-28',
    dayName: 'Dom',
    dayNumber: '28',
    month: 'Gen',
    time: '11:00 - 15:00',
    checkOut: '10:00',
    checkIn: '16:00',
    type: 'standard',
    confidence: 90,
    reason: 'Dopo 3 soggiorni, consigliata pulizia approfondita',
    selected: false
  }];
  // Available dates for the wizard
  const availableDates = [{
    date: '2024-01-22',
    dayName: 'Lun',
    dayNumber: '22',
    month: 'Gen'
  }, {
    date: '2024-01-23',
    dayName: 'Mar',
    dayNumber: '23',
    month: 'Gen'
  }, {
    date: '2024-01-24',
    dayName: 'Mer',
    dayNumber: '24',
    month: 'Gen'
  }, {
    date: '2024-01-25',
    dayName: 'Gio',
    dayNumber: '25',
    month: 'Gen'
  }, {
    date: '2024-01-26',
    dayName: 'Ven',
    dayNumber: '26',
    month: 'Gen'
  }, {
    date: '2024-01-27',
    dayName: 'Sab',
    dayNumber: '27',
    month: 'Gen'
  }, {
    date: '2024-01-28',
    dayName: 'Dom',
    dayNumber: '28',
    month: 'Gen'
  }];
  // Available time slots
  const availableTimeSlots = [{
    id: '1',
    time: '09:00 - 12:00',
    available: true
  }, {
    id: '2',
    time: '10:00 - 13:00',
    available: true
  }, {
    id: '3',
    time: '11:00 - 14:00',
    available: false
  }, {
    id: '4',
    time: '12:00 - 15:00',
    available: true
  }, {
    id: '5',
    time: '14:00 - 17:00',
    available: true
  }, {
    id: '6',
    time: '15:00 - 18:00',
    available: true
  }];
  // Cleaning types
  const cleaningTypes = [{
    id: 'standard',
    name: 'Pulizia Standard',
    credits: 1,
    duration: '3 ore',
    description: 'Pulizia completa di tutti gli ambienti'
  }, {
    id: 'deep',
    name: 'Deep Cleaning',
    credits: 2,
    duration: '5 ore',
    description: 'Pulizia profonda con sanificazione'
  }, {
    id: 'express',
    name: 'Pulizia Express',
    credits: 1,
    duration: '2 ore',
    description: 'Pulizia rapida per turnover veloci'
  }];
  const resetWizard = () => {
    setWizardMode('choice');
    setWizardStep(0);
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedType(null);
    setIsConfirming(false);
    setIsAnalyzing(false);
    setAiSuggestions([]);
    setAnalysisComplete(false);
  };
  const handleClose = () => {
    onClose();
    setTimeout(resetWizard, 300);
  };
  const handleSelectManual = () => {
    setWizardMode('manual');
    setWizardStep(1);
  };
  const handleSelectAi = () => {
    setWizardMode('ai');
    setWizardStep(1);
    setIsAnalyzing(true);
    // Simulate AI analysis
    setTimeout(() => {
      setIsAnalyzing(false);
      setAiSuggestions(mockAiSuggestions);
      setAnalysisComplete(true);
    }, 2500);
  };
  const toggleAiSuggestion = (id: string) => {
    setAiSuggestions(prev => prev.map(s => s.id === id ? {
      ...s,
      selected: !s.selected
    } : s));
  };
  const getSelectedAiSuggestions = () => {
    return aiSuggestions.filter(s => s.selected);
  };
  const getTotalCredits = () => {
    return getSelectedAiSuggestions().reduce((acc, s) => {
      return acc + (s.type === 'standard' ? 1 : 1);
    }, 0);
  };
  const handleConfirm = () => {
    setIsConfirming(true);
    setTimeout(() => {
      if (wizardMode === 'manual') {
        setWizardStep(4);
      } else {
        setWizardStep(3); // AI success step
      }
      setIsConfirming(false);
    }, 1500);
  };
  const handleBack = () => {
    if (wizardMode === 'choice' || wizardStep === 0) {
      return;
    }
    if (wizardStep === 1) {
      setWizardMode('choice');
      setWizardStep(0);
      setIsAnalyzing(false);
      setAnalysisComplete(false);
      setAiSuggestions([]);
    } else {
      setWizardStep(wizardStep - 1);
    }
  };
  const getSelectedDateInfo = () => {
    return availableDates.find(d => d.date === selectedDate);
  };
  const getSelectedTypeInfo = () => {
    return cleaningTypes.find(t => t.id === selectedType);
  };
  const getStepCount = () => {
    if (wizardMode === 'manual') return 3;
    if (wizardMode === 'ai') return 2;
    return 0;
  };
  const getHeaderTitle = () => {
    if (wizardMode === 'choice') return 'Pianifica Pulizia';
    if (wizardMode === 'ai') {
      if (wizardStep === 3) return 'Pulizie Pianificate!';
      return 'Pianificazione AI';
    }
    if (wizardStep === 4) return 'Pulizia Pianificata!';
    return 'Pianifica Pulizia';
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200" onClick={handleClose} data-id="element-604">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-id="element-605">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-200" data-id="element-606">
          <div className="flex items-center gap-3" data-id="element-607">
            {wizardStep > 0 && wizardStep < (wizardMode === 'manual' ? 4 : 3) && <button onClick={handleBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors -ml-2" data-id="element-608">
                  <ChevronLeft size={20} className="text-gray-500" data-id="element-609" />
                </button>}
            <div data-id="element-610">
              <h2 className="text-lg font-bold text-navy" data-id="element-611">
                {getHeaderTitle()}
              </h2>
              {wizardMode !== 'choice' && wizardStep > 0 && wizardStep < (wizardMode === 'manual' ? 4 : 3) && <p className="text-xs text-gray-500 mt-0.5" data-id="element-612">
                    Passo {wizardStep} di {getStepCount()}
                  </p>}
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-613">
            <X size={20} className="text-gray-500" data-id="element-614" />
          </button>
        </div>

        {/* Progress Bar */}
        {wizardMode !== 'choice' && wizardStep > 0 && wizardStep < (wizardMode === 'manual' ? 4 : 3) && <div className="px-4 lg:px-6 pt-4" data-id="element-615">
              <div className="flex gap-2" data-id="element-616">
                {Array.from({
            length: getStepCount()
          }, (_, i) => i + 1).map(step => <div key={step} className={`h-1 flex-1 rounded-full transition-colors ${step <= wizardStep ? 'bg-teal-deep' : 'bg-gray-200'}`} data-id="element-617" />)}
              </div>
            </div>}

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-618">
          {/* Step 0: Choose Mode */}
          {wizardMode === 'choice' && <div className="space-y-4" data-id="element-619">
              <div data-id="element-620">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-621">
                  Come vuoi pianificare?
                </h3>
                <p className="text-xs text-gray-500" data-id="element-622">
                  Scegli la modalità di pianificazione
                </p>
              </div>

              <div className="space-y-3" data-id="element-623">
                {/* AI Option */}
                <button onClick={handleSelectAi} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-teal-deep hover:bg-teal-50/50 transition-all group" data-id="element-624">
                  <div className="flex items-start gap-4" data-id="element-625">
                    <div className="w-12 h-12 rounded-xl bg-coral flex items-center justify-center flex-shrink-0" data-id="element-626">
                      <Sparkles size={24} className="text-white" data-id="element-627" />
                    </div>
                    <div className="flex-1" data-id="element-628">
                      <div className="flex items-center gap-2" data-id="element-629">
                        <span className="text-sm font-semibold text-navy group-hover:text-teal-deep transition-colors" data-id="element-630">
                          Pianificazione AI
                        </span>
                        <span className="text-[10px] font-medium px-2 py-0.5 bg-teal-100 text-coral rounded-full" data-id="element-631">
                          Consigliato
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-632">
                        L'AI analizza i tuoi check-in e check-out e ti propone
                        gli slot migliori per le pulizie
                      </p>
                      <div className="flex items-center gap-3 mt-2" data-id="element-633">
                        <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-634">
                          <CheckCircle size={10} className="text-teal-deep" data-id="element-635" />
                          Analisi automatica
                        </span>
                        <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-636">
                          <CheckCircle size={10} className="text-teal-deep" data-id="element-637" />
                          Più slot in una volta
                        </span>
                      </div>
                    </div>
                  </div>
                </button>

                {/* Manual Option */}
                <button onClick={handleSelectManual} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all group" data-id="element-638">
                  <div className="flex items-start gap-4" data-id="element-639">
                    <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-640">
                      <MousePointer size={24} className="text-gray-500" data-id="element-641" />
                    </div>
                    <div className="flex-1" data-id="element-642">
                      <span className="text-sm font-semibold text-navy" data-id="element-643">
                        Pianificazione Manuale
                      </span>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-644">
                        Scegli tu giorno, orario e tipo di pulizia passo per
                        passo
                      </p>
                      <div className="flex items-center gap-3 mt-2" data-id="element-645">
                        <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-646">
                          <Calendar size={10} data-id="element-647" />
                          Controllo totale
                        </span>
                        <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-648">
                          <Clock size={10} data-id="element-649" />
                          Una pulizia alla volta
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              {/* Property Info */}
              <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3 mt-4" data-id="element-650">
                <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center" data-id="element-651">
                  <Home size={18} className="text-gray-400" data-id="element-652" />
                </div>
                <div data-id="element-653">
                  <div className="text-sm font-medium text-navy" data-id="element-654">
                    Villa Sunset
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-655">
                    Proprietà selezionata
                  </div>
                </div>
              </div>
            </div>}

          {/* AI Mode - Step 1: Analysis & Suggestions */}
          {wizardMode === 'ai' && wizardStep === 1 && <div className="space-y-4" data-id="element-656">
              {isAnalyzing ? <div className="text-center py-8" data-id="element-657">
                  <div className="w-16 h-16 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4" data-id="element-658">
                    <Sparkles size={28} className="text-teal-deep animate-pulse" data-id="element-659" />
                  </div>
                  <h3 className="text-sm font-semibold text-navy mb-2" data-id="element-660">
                    Analisi in corso...
                  </h3>
                  <p className="text-xs text-gray-500 mb-4" data-id="element-661">
                    Sto analizzando i check-in e check-out per trovare gli slot
                    migliori
                  </p>
                  <div className="w-48 h-1 bg-gray-200 rounded-full mx-auto overflow-hidden" data-id="element-662">
                    <div className="h-full bg-teal-deep rounded-full animate-[loading_1.5s_ease-in-out_infinite]" style={{
                width: '60%'
              }} data-id="element-663" />
                  </div>
                </div> : <>
                  <div data-id="element-664">
                    <div className="flex items-center gap-2 mb-1" data-id="element-665">
                      <Sparkles size={16} className="text-coral" data-id="element-666" />
                      <h3 className="text-sm font-semibold text-navy" data-id="element-667">
                        Slot suggeriti dall'AI
                      </h3>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-668">
                      Seleziona gli slot che vuoi confermare
                    </p>
                  </div>

                  <div className="space-y-2" data-id="element-669">
                    {aiSuggestions.map(suggestion => <button key={suggestion.id} onClick={() => toggleAiSuggestion(suggestion.id)} className={`w-full text-left p-4 rounded-xl border-2 transition-all ${suggestion.selected ? 'border-teal-deep bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-670">
                        <div className="flex items-start gap-3" data-id="element-671">
                          {/* Checkbox */}
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${suggestion.selected ? 'bg-teal-deep border-teal-deep' : 'border-gray-300'}`} data-id="element-672">
                            {suggestion.selected && <Check size={12} className="text-white" data-id="element-673" />}
                          </div>

                          {/* Date badge */}
                          <div className="w-12 h-12 rounded-lg flex flex-col items-center justify-center bg-white border border-gray-200 flex-shrink-0" data-id="element-674">
                            <span className="text-[10px] font-medium text-gray-500 uppercase" data-id="element-675">
                              {suggestion.dayName}
                            </span>
                            <span className="text-lg font-bold leading-tight text-navy" data-id="element-676">
                              {suggestion.dayNumber}
                            </span>
                          </div>

                          {/* Details */}
                          <div className="flex-1 min-w-0" data-id="element-677">
                            <div className="flex items-center gap-2" data-id="element-678">
                              <span className="text-sm font-semibold text-navy" data-id="element-679">
                                {suggestion.time}
                              </span>
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${suggestion.type === 'standard' ? 'bg-teal-100 text-coral' : 'bg-amber-100 text-amber-700'}`} data-id="element-680">
                                {suggestion.type === 'standard' ? 'Standard' : 'Express'}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5" data-id="element-681">
                              Check-out {suggestion.checkOut} → Check-in{' '}
                              {suggestion.checkIn}
                            </div>
                            <div className="flex items-center gap-1 mt-1" data-id="element-682">
                              <Sparkles size={10} className="text-coral" data-id="element-683" />
                              <span className="text-[10px] text-gray-500" data-id="element-684">
                                {suggestion.reason}
                              </span>
                            </div>
                          </div>

                          {/* Confidence */}
                          <div className="flex items-center gap-1 px-2 py-1 bg-pink-50 rounded-lg flex-shrink-0" data-id="element-685">
                            <span className="text-xs font-bold text-coral" data-id="element-686">
                              {suggestion.confidence}%
                            </span>
                          </div>
                        </div>
                      </button>)}
                  </div>

                  {/* Summary */}
                  {getSelectedAiSuggestions().length > 0 && <div className="bg-gray-50 rounded-xl p-4 space-y-2" data-id="element-687">
                      <div className="flex justify-between text-sm" data-id="element-688">
                        <span className="text-gray-600" data-id="element-689">
                          Pulizie selezionate
                        </span>
                        <span className="font-medium text-navy" data-id="element-690">
                          {getSelectedAiSuggestions().length}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm" data-id="element-691">
                        <span className="text-gray-600" data-id="element-692">Crediti totali</span>
                        <span className="font-bold text-coral" data-id="element-693">
                          {getTotalCredits()} crediti
                        </span>
                      </div>
                    </div>}

                  {/* Credits Warning */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-id="element-694">
                    <CreditCard size={16} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-695" />
                    <div data-id="element-696">
                      <div className="text-xs font-medium text-amber-800" data-id="element-697">
                        Crediti disponibili: 13
                      </div>
                      <div className="text-xs text-amber-700 mt-0.5" data-id="element-698">
                        Dopo questa prenotazione avrai {13 - getTotalCredits()}{' '}
                        crediti
                      </div>
                    </div>
                  </div>
                </>}
            </div>}

          {/* AI Mode - Step 2: Confirmation */}
          {wizardMode === 'ai' && wizardStep === 2 && <div className="space-y-4" data-id="element-699">
              <div data-id="element-700">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-701">
                  Conferma prenotazioni
                </h3>
                <p className="text-xs text-gray-500" data-id="element-702">
                  Verifica i dettagli prima di confermare
                </p>
              </div>

              <div className="space-y-2" data-id="element-703">
                {getSelectedAiSuggestions().map(suggestion => <div key={suggestion.id} className="p-3 bg-gray-50 rounded-lg flex items-center gap-3" data-id="element-704">
                    <div className="w-10 h-10 rounded-lg flex flex-col items-center justify-center bg-white border border-gray-200" data-id="element-705">
                      <span className="text-[9px] font-medium text-gray-500 uppercase" data-id="element-706">
                        {suggestion.dayName}
                      </span>
                      <span className="text-sm font-bold leading-tight text-navy" data-id="element-707">
                        {suggestion.dayNumber}
                      </span>
                    </div>
                    <div className="flex-1" data-id="element-708">
                      <div className="text-sm font-medium text-navy" data-id="element-709">
                        {suggestion.time}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-710">
                        {suggestion.type === 'standard' ? 'Pulizia Standard' : 'Pulizia Express'}{' '}
                        · 1 credito
                      </div>
                    </div>
                    <CheckCircle size={18} className="text-teal-deep" data-id="element-711" />
                  </div>)}
              </div>

              <div className="bg-gray-50 rounded-xl p-4 space-y-2" data-id="element-712">
                <div className="flex justify-between text-sm" data-id="element-713">
                  <span className="text-gray-600" data-id="element-714">Proprietà</span>
                  <span className="font-medium text-navy" data-id="element-715">Villa Sunset</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-716">
                  <span className="text-gray-600" data-id="element-717">Pulizie totali</span>
                  <span className="font-medium text-navy" data-id="element-718">
                    {getSelectedAiSuggestions().length}
                  </span>
                </div>
                <div className="pt-2 border-t border-gray-200 flex justify-between text-sm" data-id="element-719">
                  <span className="text-gray-600" data-id="element-720">Crediti da scalare</span>
                  <span className="font-bold text-coral" data-id="element-721">
                    {getTotalCredits()} crediti
                  </span>
                </div>
              </div>
            </div>}

          {/* AI Mode - Step 3: Success */}
          {wizardMode === 'ai' && wizardStep === 3 && <div className="text-center py-6" data-id="element-722">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-723">
                <CheckCircle size={32} className="text-green-600" data-id="element-724" />
              </div>
              <h3 className="text-lg font-bold text-navy mb-2" data-id="element-725">
                {getSelectedAiSuggestions().length} Pulizie Pianificate!
              </h3>
              <p className="text-sm text-gray-600 mb-6" data-id="element-726">
                Le tue pulizie sono state programmate con successo.
              </p>

              <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2 mb-6" data-id="element-727">
                {getSelectedAiSuggestions().map(s => <div key={s.id} className="flex justify-between text-sm py-1" data-id="element-728">
                    <span className="text-gray-600" data-id="element-729">
                      {s.dayName} {s.dayNumber} {s.month}
                    </span>
                    <span className="font-medium text-navy" data-id="element-730">{s.time}</span>
                  </div>)}
                <div className="pt-2 border-t border-gray-200 flex justify-between text-sm" data-id="element-731">
                  <span className="text-gray-600" data-id="element-732">Crediti scalati</span>
                  <span className="font-bold text-coral" data-id="element-733">
                    -{getTotalCredits()} crediti
                  </span>
                </div>
              </div>

              <div className="text-xs text-gray-500" data-id="element-734">
                Riceverai una notifica di conferma via email.
              </div>
            </div>}

          {/* Manual Mode - Step 1: Select Date */}
          {wizardMode === 'manual' && wizardStep === 1 && <div className="space-y-4" data-id="element-735">
              <div data-id="element-736">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-737">
                  Seleziona il giorno
                </h3>
                <p className="text-xs text-gray-500" data-id="element-738">
                  Scegli quando vuoi programmare la pulizia
                </p>
              </div>

              <div className="grid grid-cols-7 gap-2" data-id="element-739">
                {availableDates.map(date => <button key={date.date} onClick={() => setSelectedDate(date.date)} className={`flex flex-col items-center justify-center aspect-square rounded-xl border-2 transition-all ${selectedDate === date.date ? 'border-teal-deep bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-740">
                    <span className="text-[10px] font-medium text-gray-500 uppercase" data-id="element-741">
                      {date.dayName}
                    </span>
                    <span className={`text-lg font-bold leading-tight ${selectedDate === date.date ? 'text-teal-deep' : 'text-navy'}`} data-id="element-742">
                      {date.dayNumber}
                    </span>
                    <span className="text-[10px] text-gray-400" data-id="element-743">
                      {date.month}
                    </span>
                  </button>)}
              </div>

              <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3" data-id="element-744">
                <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center" data-id="element-745">
                  <Home size={18} className="text-gray-400" data-id="element-746" />
                </div>
                <div data-id="element-747">
                  <div className="text-sm font-medium text-navy" data-id="element-748">
                    Villa Sunset
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-749">
                    Proprietà selezionata
                  </div>
                </div>
              </div>
            </div>}

          {/* Manual Mode - Step 2: Select Time */}
          {wizardMode === 'manual' && wizardStep === 2 && <div className="space-y-4" data-id="element-750">
              <div data-id="element-751">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-752">
                  Seleziona l'orario
                </h3>
                <p className="text-xs text-gray-500" data-id="element-753">
                  Scegli la fascia oraria per la pulizia
                </p>
              </div>

              <div className="space-y-2" data-id="element-754">
                {availableTimeSlots.map(slot => <button key={slot.id} onClick={() => slot.available && setSelectedTime(slot.time)} disabled={!slot.available} className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${!slot.available ? 'border-gray-100 bg-gray-50 cursor-not-allowed' : selectedTime === slot.time ? 'border-teal-deep bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-755">
                    <div className="flex items-center gap-3" data-id="element-756">
                      <Clock size={18} className={!slot.available ? 'text-gray-300' : selectedTime === slot.time ? 'text-teal-deep' : 'text-gray-400'} data-id="element-757" />
                      <span className={`text-sm font-medium ${!slot.available ? 'text-gray-400' : selectedTime === slot.time ? 'text-teal-deep' : 'text-navy'}`} data-id="element-758">
                        {slot.time}
                      </span>
                    </div>
                    {!slot.available && <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded" data-id="element-759">
                        Non disponibile
                      </span>}
                    {slot.available && selectedTime === slot.time && <CheckCircle size={18} className="text-teal-deep" data-id="element-760" />}
                  </button>)}
              </div>
            </div>}

          {/* Manual Mode - Step 3: Select Type & Confirm */}
          {wizardMode === 'manual' && wizardStep === 3 && <div className="space-y-4" data-id="element-761">
              <div data-id="element-762">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-763">
                  Tipo di pulizia
                </h3>
                <p className="text-xs text-gray-500" data-id="element-764">
                  Seleziona il tipo di intervento
                </p>
              </div>

              <div className="space-y-2" data-id="element-765">
                {cleaningTypes.map(type => <button key={type.id} onClick={() => setSelectedType(type.id)} className={`w-full text-left p-4 rounded-xl border-2 transition-all ${selectedType === type.id ? 'border-teal-deep bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-766">
                    <div className="flex items-start justify-between" data-id="element-767">
                      <div data-id="element-768">
                        <div className={`text-sm font-semibold ${selectedType === type.id ? 'text-teal-deep' : 'text-navy'}`} data-id="element-769">
                          {type.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5" data-id="element-770">
                          {type.description}
                        </div>
                        <div className="text-xs text-gray-400 mt-1" data-id="element-771">
                          Durata: {type.duration}
                        </div>
                      </div>
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-lg ${selectedType === type.id ? 'bg-teal-100' : 'bg-gray-100'}`} data-id="element-772">
                        <CreditCard size={12} className={selectedType === type.id ? 'text-teal-deep' : 'text-gray-500'} data-id="element-773" />
                        <span className={`text-xs font-bold ${selectedType === type.id ? 'text-teal-deep' : 'text-gray-600'}`} data-id="element-774">
                          {type.credits} credito{type.credits > 1 ? 'i' : ''}
                        </span>
                      </div>
                    </div>
                  </button>)}
              </div>

              {selectedType && <div className="bg-gray-50 rounded-xl p-4 space-y-3" data-id="element-775">
                  <div className="text-xs text-gray-400 uppercase tracking-wider font-medium" data-id="element-776">
                    Riepilogo
                  </div>
                  <div className="space-y-2" data-id="element-777">
                    <div className="flex justify-between text-sm" data-id="element-778">
                      <span className="text-gray-600" data-id="element-779">Proprietà</span>
                      <span className="font-medium text-navy" data-id="element-780">
                        Villa Sunset
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-781">
                      <span className="text-gray-600" data-id="element-782">Data</span>
                      <span className="font-medium text-navy" data-id="element-783">
                        {getSelectedDateInfo()?.dayName}{' '}
                        {getSelectedDateInfo()?.dayNumber}{' '}
                        {getSelectedDateInfo()?.month}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-784">
                      <span className="text-gray-600" data-id="element-785">Orario</span>
                      <span className="font-medium text-navy" data-id="element-786">
                        {selectedTime}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-787">
                      <span className="text-gray-600" data-id="element-788">Tipo</span>
                      <span className="font-medium text-navy" data-id="element-789">
                        {getSelectedTypeInfo()?.name}
                      </span>
                    </div>
                    <div className="pt-2 border-t border-gray-200 flex justify-between text-sm" data-id="element-790">
                      <span className="text-gray-600" data-id="element-791">Crediti da scalare</span>
                      <span className="font-bold text-coral" data-id="element-792">
                        {getSelectedTypeInfo()?.credits} credito
                        {(getSelectedTypeInfo()?.credits || 0) > 1 ? 'i' : ''}
                      </span>
                    </div>
                  </div>
                </div>}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-id="element-793">
                <CreditCard size={16} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-794" />
                <div data-id="element-795">
                  <div className="text-xs font-medium text-amber-800" data-id="element-796">
                    Crediti disponibili: 13
                  </div>
                  <div className="text-xs text-amber-700 mt-0.5" data-id="element-797">
                    Dopo questa prenotazione avrai{' '}
                    {13 - (getSelectedTypeInfo()?.credits || 0)} crediti
                  </div>
                </div>
              </div>
            </div>}

          {/* Manual Mode - Step 4: Success */}
          {wizardMode === 'manual' && wizardStep === 4 && <div className="text-center py-6" data-id="element-798">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-799">
                <CheckCircle size={32} className="text-green-600" data-id="element-800" />
              </div>
              <h3 className="text-lg font-bold text-navy mb-2" data-id="element-801">
                Pulizia Pianificata!
              </h3>
              <p className="text-sm text-gray-600 mb-6" data-id="element-802">
                La tua pulizia è stata programmata con successo.
              </p>

              <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2 mb-6" data-id="element-803">
                <div className="flex justify-between text-sm" data-id="element-804">
                  <span className="text-gray-600" data-id="element-805">Proprietà</span>
                  <span className="font-medium text-navy" data-id="element-806">Villa Sunset</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-807">
                  <span className="text-gray-600" data-id="element-808">Data</span>
                  <span className="font-medium text-navy" data-id="element-809">
                    {getSelectedDateInfo()?.dayName}{' '}
                    {getSelectedDateInfo()?.dayNumber}{' '}
                    {getSelectedDateInfo()?.month}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-810">
                  <span className="text-gray-600" data-id="element-811">Orario</span>
                  <span className="font-medium text-navy" data-id="element-812">{selectedTime}</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-813">
                  <span className="text-gray-600" data-id="element-814">Tipo</span>
                  <span className="font-medium text-navy" data-id="element-815">
                    {getSelectedTypeInfo()?.name}
                  </span>
                </div>
                <div className="pt-2 border-t border-gray-200 flex justify-between text-sm" data-id="element-816">
                  <span className="text-gray-600" data-id="element-817">Crediti scalati</span>
                  <span className="font-bold text-coral" data-id="element-818">
                    -{getSelectedTypeInfo()?.credits} credito
                    {(getSelectedTypeInfo()?.credits || 0) > 1 ? 'i' : ''}
                  </span>
                </div>
              </div>

              <div className="text-xs text-gray-500" data-id="element-819">
                Riceverai una notifica di conferma via email.
              </div>
            </div>}
        </div>

        {/* Modal Footer */}
        <div className="p-4 lg:p-6 border-t border-gray-200" data-id="element-820">
          {/* Choice mode - no footer */}
          {wizardMode === 'choice' && <div className="text-xs text-center text-gray-400" data-id="element-821">
              Seleziona una modalità per continuare
            </div>}

          {/* AI Mode - Step 1 */}
          {wizardMode === 'ai' && wizardStep === 1 && !isAnalyzing && <button onClick={() => setWizardStep(2)} disabled={getSelectedAiSuggestions().length === 0} className={`w-full px-4 py-3 rounded-xl text-sm font-medium transition-colors min-h-[48px] ${getSelectedAiSuggestions().length > 0 ? 'bg-coral text-white hover:bg-coral-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-822">
              Continua con {getSelectedAiSuggestions().length} pulizie
            </button>}

          {/* AI Mode - Step 2 */}
          {wizardMode === 'ai' && wizardStep === 2 && <button onClick={handleConfirm} disabled={isConfirming} className={`w-full px-4 py-3 rounded-xl text-sm font-medium transition-colors min-h-[48px] flex items-center justify-center gap-2 ${!isConfirming ? 'bg-coral text-white hover:bg-coral-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-823">
              {isConfirming ? <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-824" />
                  Conferma in corso...
                </> : <>
                  <CheckCircle size={18} data-id="element-825" />
                  Conferma e Scala {getTotalCredits()} Crediti
                </>}
            </button>}

          {/* AI Mode - Step 3 (Success) */}
          {wizardMode === 'ai' && wizardStep === 3 && <div className="flex gap-3" data-id="element-826">
              <Link to="/pulizie" onClick={handleClose} className="flex-1 px-4 py-3 bg-gray-100 text-navy rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors min-h-[48px] flex items-center justify-center" data-id="element-827">
                Vai alle Pulizie
              </Link>
              <button onClick={handleClose} className="w-full px-4 py-3 bg-coral text-white rounded-xl text-sm font-medium hover:bg-coral-600 transition-colors min-h-[48px]" data-id="element-828">
                Chiudi
              </button>
            </div>}

          {/* Manual Mode - Step 1 */}
          {wizardMode === 'manual' && wizardStep === 1 && <button onClick={() => setWizardStep(2)} disabled={!selectedDate} className={`w-full px-4 py-3 rounded-xl text-sm font-medium transition-colors min-h-[48px] ${selectedDate ? 'bg-coral text-white hover:bg-coral-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-829">
              Continua
            </button>}

          {/* Manual Mode - Step 2 */}
          {wizardMode === 'manual' && wizardStep === 2 && <button onClick={() => setWizardStep(3)} disabled={!selectedTime} className={`w-full px-4 py-3 rounded-xl text-sm font-medium transition-colors min-h-[48px] ${selectedTime ? 'bg-coral text-white hover:bg-coral-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-830">
              Continua
            </button>}

          {/* Manual Mode - Step 3 */}
          {wizardMode === 'manual' && wizardStep === 3 && <button onClick={handleConfirm} disabled={!selectedType || isConfirming} className={`w-full px-4 py-3 rounded-xl text-sm font-medium transition-colors min-h-[48px] flex items-center justify-center gap-2 ${selectedType && !isConfirming ? 'bg-coral text-white hover:bg-coral-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-831">
              {isConfirming ? <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-832" />
                  Conferma in corso...
                </> : <>
                  <CheckCircle size={18} data-id="element-833" />
                  Conferma e Scala Crediti
                </>}
            </button>}

          {/* Manual Mode - Step 4 (Success) */}
          {wizardMode === 'manual' && wizardStep === 4 && <div className="flex gap-3" data-id="element-834">
              <Link to="/pulizie" onClick={handleClose} className="flex-1 px-4 py-3 bg-gray-100 text-navy rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors min-h-[48px] flex items-center justify-center" data-id="element-835">
                Vai alle Pulizie
              </Link>
              <button onClick={handleClose} className="w-full px-4 py-3 bg-coral text-white rounded-xl text-sm font-medium hover:bg-coral-600 transition-colors min-h-[48px]" data-id="element-836">
                Chiudi
              </button>
            </div>}
        </div>
      </div>
    </div>;
}